/**
 * Misconception Registry — module-based aggregation.
 *
 * The registry is split per module (rek, get, spr, sww, met, tijd, geld, taa)
 * to keep each file maintainable. This index concatenates all per-module
 * arrays and exposes the same public API that consumers used to import from
 * `../misconceptions`.
 *
 * INTEGRITY (mei 2026): the aggregator is self-healing + observable.
 *  1. DEDUPE: entries are deduplicated by `id` (first occurrence wins) when
 *     the registry is assembled. A sub-file that accidentally double-exports
 *     (the taa.ts corruption of mei 2026) can therefore no longer corrupt the
 *     by-id lookup table — the duplicates are dropped and reported.
 *  2. REPORT (non-fatal): at module load we log a loud, structured warning
 *     listing any duplicate IDs, malformed IDs (must match `module.kebab-slug`)
 *     and entries with missing required fields. This is intentionally a WARN,
 *     not a throw: the registry ships with a known deferred kebab-migration
 *     backlog (SSOT Fase B / P3 — dotted IDs like `taa.arg.feit.als.argument`),
 *     and a hard throw would turn that documented data-debt into an app-crash.
 */

import type { MisconceptionDefinition } from './types';
import { REK_MISCONCEPTIONS } from './rek';
import { GET_MISCONCEPTIONS } from './get';
import { SPR_MISCONCEPTIONS } from './spr';
import { SWW_MISCONCEPTIONS } from './sww';
import { MET_MISCONCEPTIONS } from './met';
import { TIJD_MISCONCEPTIONS } from './tijd';
import { GELD_MISCONCEPTIONS } from './geld';
import { TAA_MISCONCEPTIONS } from './taa';
import { TOPO_MISCONCEPTIONS } from './topo';
import { STU_MISCONCEPTIONS } from './stu';
export type { MisconceptionDefinition } from './types';

// ─── Raw per-module aggregation (may contain duplicates / malformed IDs) ─────────

const RAW_MISCONCEPTIONS: MisconceptionDefinition[] = [...REK_MISCONCEPTIONS, ...GET_MISCONCEPTIONS, ...SPR_MISCONCEPTIONS, ...SWW_MISCONCEPTIONS, ...MET_MISCONCEPTIONS, ...TIJD_MISCONCEPTIONS, ...GELD_MISCONCEPTIONS, ...TAA_MISCONCEPTIONS, ...TOPO_MISCONCEPTIONS, ...STU_MISCONCEPTIONS];

// ─── Integrity report + dedupe (run once, at module load) ────────────────────────

/**
 * Canonical ID format: lowercase module prefix, dot, kebab-slug.
 * Examples that pass: `rek.tafel-vergeten`, `get.komma-3-cijfers`.
 * Examples that fail: `REK.foo`, `rek.Foo`, `rek_foo`, `rek.arg.feit.als.argument`.
 *
 * NOTE: dotted IDs (more than one dot) are currently flagged as malformed but
 * tolerated — they are the SSOT Fase B kebab-migration backlog.
 */
const MISCONCEPTION_ID_PATTERN = /^[a-z]+\.[a-z0-9]+(?:-[a-z0-9]+)*$/;

/**
 * Deduplicate the raw registry by `id` (first occurrence wins) and emit a
 * loud, structured — but NON-FATAL — report of any integrity problems found.
 *
 * Returns the deduplicated registry that becomes the canonical export.
 */
function buildAndReportRegistry(raw: MisconceptionDefinition[]): MisconceptionDefinition[] {
  const byId = new Map<string, MisconceptionDefinition>();
  const duplicateIds: string[] = [];
  const conflictingIds: string[] = [];
  const malformedIds: string[] = [];
  const missingFields: string[] = [];
  for (const entry of raw) {
    // 1. Dedupe by id (first wins)
    const existing = byId.get(entry.id);
    if (existing) {
      duplicateIds.push(entry.id);

      // Er werd alleen op `id` vergeleken, nooit op INHOUD. Twee soorten
      // dubbeling zien er dan identiek uit in het rapport, terwijl ze
      // volstrekt verschillende gevolgen hebben:
      //
      //   • Zelfde id, zelfde uitleg — een dubbele export. Onschuldig.
      //   • Zelfde id, ANDERE uitleg — dan bepaalt de importvolgorde welke
      //     uitleg een kind krijgt bij zijn fout. Dat is een inhoudelijke
      //     keuze die per ongeluk door een sorteervolgorde wordt gemaakt.
      //
      // Alleen het tweede geval is een echt probleem, en juist dat viel weg in
      // hetzelfde getal als het eerste.
      if (existing.label !== entry.label || existing.description !== entry.description || existing.module !== entry.module) {
        conflictingIds.push(entry.id);
      }
    } else {
      byId.set(entry.id, entry);
    }

    // 2. Format check (report-only)
    if (!MISCONCEPTION_ID_PATTERN.test(entry.id)) {
      malformedIds.push(entry.id);
    }

    // 3. Required-field presence (report-only)
    const missing: string[] = [];
    if (!entry.label || !entry.label.trim()) missing.push('label');
    if (!entry.module || !entry.module.trim()) missing.push('module');
    if (!entry.description || !entry.description.trim()) missing.push('description');
    if (missing.length > 0) {
      missingFields.push(`${entry.id} (missing: ${missing.join(', ')})`);
    }
  }

  // Collapse duplicate-id list to unique ids with counts for readability.
  if (duplicateIds.length > 0 || malformedIds.length > 0 || missingFields.length > 0) {
    const uniqueDup = Array.from(new Set(duplicateIds));
    const uniqueConflict = Array.from(new Set(conflictingIds));
    const uniqueMalformed = Array.from(new Set(malformedIds));
    /* eslint-disable no-console */
    console.warn(`[MisconceptionRegistry] Integrity report (non-fatal):\n` + `  • Duplicate IDs dropped (kept first occurrence): ${duplicateIds.length} dropped, ${uniqueDup.length} unique\n` + (uniqueDup.length > 0 ? `      ${uniqueDup.slice(0, 30).join(', ')}${uniqueDup.length > 30 ? `, …(+${uniqueDup.length - 30})` : ''}\n` : '') + (
    // Apart en met nadruk: dit is het enige geval waarin een kind een
    // andere uitleg krijgt dan het seedbestand bedoelde.
    uniqueConflict.length > 0 ? `  ⚠️  Duplicates met VERSCHILLENDE inhoud: ${uniqueConflict.length}\n` + `      ${uniqueConflict.slice(0, 30).join(', ')}${uniqueConflict.length > 30 ? `, …(+${uniqueConflict.length - 30})` : ''}\n` + `      Hier bepaalt de importvolgorde welke uitleg een kind ziet. Los dit als eerste op.\n` : '  • Duplicates met verschillende inhoud: 0 (de rest zijn dubbele exports)\n') + `  • Malformed IDs (kebab-migration backlog — SSOT Fase B): ${uniqueMalformed.length}\n` + (uniqueMalformed.length > 0 ? `      ${uniqueMalformed.slice(0, 30).join(', ')}${uniqueMalformed.length > 30 ? `, …(+${uniqueMalformed.length - 30})` : ''}\n` : '') + `  • Entries missing required fields: ${missingFields.length}\n` + (missingFields.length > 0 ? `      ${missingFields.slice(0, 30).join(', ')}\n` : '') + `  Fix duplicates in the per-module files (a double-export is the usual cause). ` + `Malformed dotted IDs are a tracked backlog and do not break the app.`);
    /* eslint-enable no-console */
  }
  return Array.from(byId.values());
}

// ─── Central misconception registry (deduplicated) ──────────────────────────────

export const MISCONCEPTION_REGISTRY: MisconceptionDefinition[] = buildAndReportRegistry(RAW_MISCONCEPTIONS);

// ─── Helper functions ──────────────────────────────────────────────────────────

const MISCONCEPTION_BY_ID = new Map<string, MisconceptionDefinition>(MISCONCEPTION_REGISTRY.map(m => [m.id, m]));

/** Look up a misconception by id. Returns undefined if not registered. */
export function getMisconception(id: string): MisconceptionDefinition | undefined {
  return MISCONCEPTION_BY_ID.get(id);
}

/** Returns true if the given id is registered in the misconception registry. */
export function isKnownMisconception(id: string): boolean {
  return MISCONCEPTION_BY_ID.has(id);
}

/** Returns all misconceptions for a given module label (e.g. 'Metriek'). */
export function getMisconceptionsForModule(module: string): MisconceptionDefinition[] {
  return MISCONCEPTION_REGISTRY.filter(m => m.module === module);
}

/** Returns all registered misconception ids. */
export function getAllMisconceptionIds(): string[] {
  return MISCONCEPTION_REGISTRY.map(m => m.id);
}